<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="calendar" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Leave Balances</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm"
            @click="openAddModal"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Leave Balance
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 bg-white">
          <div class="d-flex flex-wrap gap-3">
            <div class="flex-grow-1">
              <label class="form-label">Employee</label>
              <v-select
                label="name"
                v-model="filters.employee_id"
                :reduce="employee => employee.id"
                :options="employees"
                class="style-chooser"
                placeholder="Type to search"
                @option:selected="filterLeaveBalances"
              ></v-select>
            </div>
            <div class="flex-grow-1">
              <label class="form-label">Year</label>
              <select class="form-select" v-model="filters.year" @change="filterLeaveBalances" data="">
                <option value="">All Years</option>
                <option v-for="year in years" :key="year" :value="year">Year {{ year }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-12 mt-3 text-end">
            <button class="btn btn-outline-secondary" @click="resetFilter">
              <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
              Reset Filters
            </button>
          </div>
        </Card>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="calendar" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Leave Balances ({{leaveBalances.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search leave balance" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Balances Table -->
          <div v-if="!loading && leaveBalances.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Employee</th>
                  <th scope="col" class="fw-semibold">Leave Type</th>
                  <th scope="col" class="fw-semibold">Entitled</th>
                  <th scope="col" class="fw-semibold">Used</th>
                  <th scope="col" class="fw-semibold">Pending</th>
                  <th scope="col" class="fw-semibold">Remaining</th>
                  <th scope="col" class="fw-semibold">Year</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(balance, index) in filteredLeaveBalances" :key="balance.id">
                  <td>
                    <a :href="`/employee/${balance.employee.id}`" class="fw-semibold">
                      {{ balance.employee.first_name }} {{ balance.employee.middle_name }} {{ balance.employee.last_name }}
                    </a>
                  </td>
                  <td>{{ balance.leave_type.name }}</td>
                  <td>{{ balance.entitled_days }} days</td>
                  <td>{{ balance.used_days }} days</td>
                  <td>{{ balance.pending_days }} days</td>
                  <td>
                    <span :class="getRemainingDaysClass(balance)">
                      {{ balance.entitled_days - balance.used_days - balance.pending_days }} days
                    </span>
                  </td>
                  <td>{{ balance.year }}</td>
                  <td class="text-end">
                    <button
                      v-if="profile.role !== 'user'"
                      class="btn btn-sm btn-outline-primary"
                      @click="editLeaveBalance(balance)"
                    >
                      <vue-feather type="edit" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && leaveBalances.length === 0" class="text-center py-5">
            <vue-feather type="calendar" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Leave Balances Found</h5>
            <p class="text-muted">Create your first leave balance to get started.</p>
            <button
              v-if="profile.role !== 'user'"
              class="btn btn-primary"
              @click="openAddModal"
            >
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Leave Balance
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add/Edit Leave Balance Modal -->
    <Modal
      :open="showModal"
      @closed="closeModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          {{ isEditing ? 'Edit Leave Balance' : 'Add Leave Balance' }}
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveLeaveBalance">
          <div class="mb-3">
            <label for="employee_id" class="form-label">Employee <span class="text-danger">*</span></label>
            <v-select
                label="name"
                :disabled="isEditing"
                v-model="form.employee_id"
                :reduce="employee => employee.id"
                :options="employees"
                class="style-chooser"
                placeholder="Type to search"
            ></v-select>
          </div>
          <div class="mb-3">
            <label for="leave_type_id" class="form-label">Leave Type <span class="text-danger">*</span></label>
            <select class="form-select" id="leave_type_id" v-model="form.leave_type_id" required :disabled="isEditing" data="">
              <option value="">Select Leave Type</option>
              <option v-for="type in leaveTypes" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
          <div v-if="isEditing" class="mb-3">
            <label for="entitled_days" class="form-label">Entitled Days <span class="text-danger">*</span></label>
            <input type="number" class="form-control" id="entitled_days" v-model="form.entitled_days" min="0" step="0.5" required>
          </div>
          <div class="mb-3">
            <label for="year" class="form-label">Year <span class="text-danger">*</span></label>
            <select class="form-select" id="year" v-model="form.year" required :disabled="isEditing" data="">
              <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import leaveService from '@/api/leaveService';
import {decrypt} from '@/utils/crypto';
import notify from '@/utils/notify';
import VueFeather from 'vue-feather';
import analyticsService from "@/api/analyticsService";
import 'vue-select/dist/vue-select.css';
import selectSearch from "@/utils/selectSearch";

export default {
  name: 'LeaveBalances',
  components: { VueFeather },
  data() {
    return {
      loading: true,
      saving: false,
      leaveBalances: [],
      leaveTypes: [],
      employees: [],
      dataTableSearch: '',
      filters: {
        employee_id: '',
        year: ''
      },
      years: [],
      showModal: false,
      isEditing: false,
      selectedLeaveBalance: null,
      profile: {},
      form: {
        employee_id: '',
        leave_type_id: '',
        entitled_days: 0,
        year: new Date().getFullYear()
      },
    };
  },
  computed: {
    filteredLeaveBalances() {
      if (!this.dataTableSearch) return this.leaveBalances;

      const search = this.dataTableSearch.toLowerCase();
      return this.leaveBalances.filter(balance =>
        (balance.employee.first_name + ' ' + balance.employee.last_name).toLowerCase().includes(search) ||
        balance.leave_type.name.toLowerCase().includes(search)
      );
    }
  },
  methods: {
    getRemainingDaysClass(balance) {
      const remaining = balance.entitled_days - balance.used_days - balance.pending_days;
      if (remaining <= 0) {
        return 'badge bg-danger';
      } else if (remaining < 5) {
        return 'badge bg-warning';
      } else {
        return 'badge bg-success';
      }
    },
    async fetchLeaveBalances() {
      try {
        this.loading = true;
        this.leaveBalances = await leaveService.getLeaveBalances();
      } catch (error) {
        notify.alert.error('Failed to fetch leave balances');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async filterLeaveBalances() {
      try {
        this.loading = true;
        this.leaveBalances = await leaveService.filterLeaveBalances(this.filters);
      } catch (error) {
        notify.alert.error('Failed to filter leave balances');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchLeaveTypes() {
      try {
        this.leaveTypes = await leaveService.getLeaveTypes();
      } catch (error) {
        notify.alert.error('Failed to fetch leave types');
        console.error(error);
      }
    },
    async fetchEmployees() {
      this.employees = await selectSearch.allEmployees()
    },
    openAddModal() {
      this.isEditing = false;
      this.form = {
        employee_id: '',
        leave_type_id: '',
        entitled_days: 0,
        year: new Date().getFullYear()
      };
      this.showModal = true;
    },
    editLeaveBalance(balance) {
      this.isEditing = true;
      this.selectedLeaveBalance = balance;
      this.form = {
        employee_id: balance.employee_id,
        leave_type_id: balance.leave_type_id,
        entitled_days: balance.entitled_days,
        year: balance.year
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveLeaveBalance() {
      try {
        this.saving = true;

        if (this.isEditing) {
          await leaveService.updateLeaveBalance(this.selectedLeaveBalance.id, this.form);
        } else {
          await leaveService.createLeaveBalance(this.form);
        }

        this.closeModal();
        await this.fetchLeaveBalances();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to save leave balance');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async resetFilter() {
      window.location.reload()
    },
  },
  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.years = await analyticsService.getYears()
    await this.fetchLeaveBalances();
    await this.fetchLeaveTypes();
    await this.fetchEmployees();
  }
};
</script>

<style>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background-color: #f2f2f2;
  border: 1px solid #dee2e6;
  border-radius: 0.4rem;
}

.style-chooser .vs__clear,
.style-chooser .vs__open-indicator {
  fill: #394066;
}
</style>
